<template>
    <v-row
        no-gutters
        class="px-9 pt-4"
    >
        <v-col cols="12">
            <v-row no-gutters class="ml-2 mb-2 mt-2 px-9">
                <v-col cols="12" class="pl-1 pt-1 pb-2">
                    <p class="title">{{ $t('Replace tax') }}</p>
                </v-col>
            </v-row>

            <v-row no-gutters class="ml-2 mb-2 mt-2 px-9">
                <v-col cols="12" class="pl-1 pt-1 pb-2">
                    <dropdown
                        :items="clientGroups"
                        :selected="selectedClientGroups"
                        name-key="name"
                        value-key="id"
                        multi
                        deselect
                        show-selected-first
                        placeholder="All client groups"
                        :label="$t('Client groups')"
                        @input="onClientGroupChanged"
                    />
                </v-col>
            </v-row>
            <v-row no-gutters class="ml-2 mb-2 mt-2 px-9">
                <v-col cols="12" class="pl-1 pt-1 pb-2">
                    <ComboboxDropdown
                        :items="taxes ? taxes : []"
                        :selected="taxes ? taxes.filter(x => x.id === oldTaxId) : []"
                        :label="'* ' + $t('Old tax')"
                        name-key="info"
                        value-key="id"
                        :sort="false"
                        show-selected-first
                        :searchable="false"
                        @input="onOldTaxChange"
                    />
                </v-col>
            </v-row>
            <v-row no-gutters class="ml-2 mb-2 mt-2 px-9">
                <v-col cols="12" class="pl-1 pt-1 pb-2">
                    <ComboboxDropdown
                        :items="taxes ? taxes : []"
                        :selected="taxes ? taxes.filter(x => x.id === newTaxId) : []"
                        :label="'* ' + $t('New tax')"
                        name-key="info"
                        value-key="id"
                        :sort="false"
                        show-selected-first
                        :searchable="false"
                        @input="onNewTaxChange"
                    />
                </v-col>
            </v-row>
            <v-row v-if="oldTaxId !== null && newTaxId !== null" no-gutters class="ml-2 mb-2 mt-2 px-9">
                <v-col cols="12" class="pl-1 pt-1 pb-2">
                    {{ $t('Find tax') }} <strong>{{ findDescription(oldTaxId) }}</strong><br>
                    {{ $t('Replace tax') }} <strong>{{ findDescription(newTaxId) }}</strong>
                </v-col>
            </v-row>

            <v-row no-gutters class="ml-2 mb-2 mt-2 px-9">
                <v-col cols="12" class="pl-1 pt-1 pb-2">
                    <v-row no-gutters class="mb-2">
                        <switch-button
                            :value="replace_tax_products"
                            :label="$t('Replace VAT in products')"
                            @change="replace_tax_products = $event; onToggle($event)"
                        />
                    </v-row>
                    <v-row no-gutters class="mb-2">
                        <switch-button
                            :value="replace_tax_master_contracts"
                            :label="$t('Replace VAT in master contracts')"
                            @change="replace_tax_master_contracts = $event; onToggle($event)"
                        />
                    </v-row>
                    <v-row no-gutters class="mb-2">
                        <switch-button
                            :value="replace_tax_client_contracts"
                            :label="$t('Replace VAT in client contracts & draft invoices')"
                            @change="replace_tax_client_contracts = $event; onToggle($event)"
                        />
                    </v-row>
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<script>
import {$t} from "@/i18n";
import Vue from "vue";
import axios from "axios";
import {computed, ref, onMounted, watch} from '@vue/composition-api'
import AppModule from "@/store/modules/app";
import DialogModule from "@/store/modules/dialog";
import BillingDialogContentHeader from '@/components/AppStore/BillingDialogContentHeader'
import appStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import AccountModule, {state} from "../../../store/modules/account";
import ProductFieldModule from "../../../store/modules/product_field";
import ClientGroupModule from "../../../store/modules/client_groups";
import AppStoreListHeader from "../../../components/AppStore/AppStoreListHeader.vue";
import ComboboxDropdown from "../../../components/ComboboxDropdown.vue";
import ApiTax from "../../../api/billing/tax";

export default {
    name: "BillingDialogToolsTab",
    components: {
        ComboboxDropdown,
        AppStoreListHeader,
        BillingDialogContentHeader,
    },
    props: {
        isSelected: {
            type: Boolean,
            default: false
        }
    },
    setup(props, ctx) {
        const loading = ref(false)
        const oldTaxId = ref(null)
        const newTaxId = ref(null)
        const filterClientGroup = ref({topics: []})
        const replace_tax_products = ref(false)
        const replace_tax_master_contracts = ref(false)
        const replace_tax_client_contracts = ref(false)

        const {fetchAccount} = AccountModule()
        const {handleErrorResponse} = AppModule()
        const {fetchAccountProductFields} = ProductFieldModule()
        const {client_groups_by_account, fetchClientGroups, list} = ClientGroupModule();
        const {appStoreDialogGetters, appStoreDialogActions} = appStoreDialog()
        const {setConfirmOptions, openConfirmDialog} = DialogModule();
        const {openDialog, setDialogOptions,} = DialogModule();

        const selectedAccount = computed(() => {
            return appStoreDialogGetters.getSelectedCompany()
        })

        const taxes = ref(null)
        onMounted(async () => {
            const companyId = selectedAccount.value.id
            taxes.value = await ApiTax.getTaxes(companyId)
        })

        const clientGroups = computed(() => {
            const client_groups = client_groups_by_account(selectedAccount.value.id)
            return client_groups.value
        });

        const selectedClientGroups = computed(() => {
            return clientGroups.value.filter(x => filterClientGroup.value.topics.includes(x.id))
        })

        const onClientGroupChanged = (data) => {
            filterClientGroup.value.topics = data.map(x => x.id);
        }

        const findDescription = (tax_id) => {
            if (!taxes) return '';
            let match = taxes.value.filter(x => x.id === tax_id)
            if (match && match.length) {
                let tax = match[0]
                return tax.name
            }
            return ''
        }

        const onToggle = (e) => {
            checkDisabled()
        }

        const onOldTaxChange = (data) => {
            oldTaxId.value = data.id
            checkDisabled()
        }

        const onNewTaxChange = (data) => {
            newTaxId.value = data.id
            checkDisabled()
        }

        const onReplaceClick = () => {
            setConfirmOptions({
                title: $t("Replace VAT values"),
                text: $t("This action cannot be undone"),
                confirm_text: $t("Replace"),
                cancel_text: $t("Cancel"),
                action: () => replaceVats(),
            });
            openConfirmDialog()
        };

        const replaceVats = () => {
            loading.value = true
            let payload = {
                "company_id": selectedAccount.value.id,
                "old_tax_id": oldTaxId.value,
                "new_tax_id": newTaxId.value,
                "client_group_id_list": selectedClientGroups.value ? selectedClientGroups.value.map(x => x.id) : [],
                "replace_tax_products": replace_tax_products.value ? 1 : 0,
                "replace_tax_master_contracts": replace_tax_master_contracts.value ? 1 : 0,
                "replace_tax_client_contracts": replace_tax_client_contracts.value ? 1 : 0,
            }
            let url = Vue.config.url_prefix + "/api/tools/replace_tax/{0}".format(selectedAccount.value.id)
            new Promise(function (resolve, reject) {
                axios
                    .post(url, payload)
                    .then(function (resp) {
                        // console.info(resp.data)
                        setDialogOptions({
                            component: () => import("@/views/Dialog/WarningDialog"),
                            payload: {
                                type: "dashboard",
                                title: $t("Result"),
                                description: resp.data['description'],
                                hasOneButton: true,
                                class_name: '',
                            }
                        });
                        openDialog();
                        loading.value = false
                    })
                    .catch(function (ex) {
                        loading.value = false
                        errorDialog.showError(ex)
                        reject();
                    })
            })
        }

        const fetchData = function () {
            fetchAccount(selectedAccount.value.id, {all: 1})
                .catch(ex => errorDialog.showError(ex))
            fetchAccountProductFields(selectedAccount.value.id)
                .catch(ex => errorDialog.showError(ex))
            fetchClientGroups(selectedAccount.value.id, {include_inactive_account: 1})
                .catch(ex => errorDialog.showError(ex))
        }

        const isDisabled = ref(true)
        const checkDisabled = () => {
            const x = oldTaxId.value === null || !newTaxId.value === null
            const y = !replace_tax_products.value && !replace_tax_master_contracts.value && !replace_tax_client_contracts.value
            isDisabled.value = x || y
        }

        watch (() => isDisabled.value, () => {
            if (isDisabled.value) {
                appStoreDialogActions.setFooterActions([]);
            } else {
                appStoreDialogActions.setFooterActions([{
                    text: $t("Continue"),
                    class: "primary",
                    loading: loading,
                    click: onReplaceClick,
                }]);
            }
        }, { immediate: true })

        fetchData()

        return {
            loading,
            isDisabled,
            onToggle,
            taxes,
            oldTaxId,
            newTaxId,
            onOldTaxChange,
            onNewTaxChange,

            clientGroups,
            filterClientGroup,
            selectedClientGroups,
            onClientGroupChanged,

            replace_tax_products,
            replace_tax_master_contracts,
            replace_tax_client_contracts,

            onReplaceClick,
            findDescription,
        };
    }
};
</script>
